import { DOCUMENT, ElementRef, Injectable, inject } from '@angular/core';
import { DragEvent } from '@interactjs/actions/drag/plugin';
import { Actions, ofType } from '@ngrx/effects';
import interact from 'interactjs';
import { clone } from 'lodash-es';
import { assert } from '../../../../core/assert/assert';
import { AbsencePlannerEventModel } from '../../../../core/models/absence-planner/absence-planner-event.model';
import { TimeBlockAbsenceModel } from '../../../../core/models/timeblock/time-block.model';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../../../core/services/controls/shive-notification.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import * as fromTimeBlockActions from '../../../../core/state/time-blocks/time-block-entries/time-blocks.actions';
import { DragOrResizeInteraction } from '../../../../shared/data-types/interact.types';

import { TimeBlockItemBuilderService } from '../../../calendar/components/time-block/generation/time-block-item-builder.service';
import { TimeBlockComponent } from '../../../calendar/components/time-block/time-block.component';
import { TimeBlockContentType } from '../../../calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { AbsencePlannerService } from '../../services/absence-planner.service';
import {
  AbsencePlannerFixedColumnWidth,
  ActiveDragOrResizeEvent,
  LeftHandlePlannerEvent,
  ResizeHandlePlannerEvent,
} from '../../utils/absence-planner.constants';
import { AbsencePlannerEventComponentHandlerService } from './absence-planner-event-component-handler.service';
import { AbsencePlannerEventCrudService } from './absence-planner-event-crud.service';

@Injectable()
export class AbsencePlannerEventDragResizeService {
  public interaction = DragOrResizeInteraction.None;
  private plannerEventBeforeDrag: AbsencePlannerEventModel = null;
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly absencePlannerService = inject(AbsencePlannerService);
  private readonly plannerEventComponentHandlerService = inject(
    AbsencePlannerEventComponentHandlerService,
  );
  private readonly plannerEventCrudService = inject(AbsencePlannerEventCrudService);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private slotWidth = -1;
  private beforeDragMouseSlotIndex = -1;
  private beforeResizeLeftPos = -1;
  private currentlyDraggedEventModel: AbsencePlannerEventModel = null;
  private readonly document = inject(DOCUMENT);
  private readonly actions$ = inject(Actions);

  constructor() {
    this.initHttpCallbacks();
  }

  public initializeDragAndDrop(element: ElementRef<HTMLElement>, slotWidth: number): void {
    this.slotWidth = slotWidth;
    interact(element.nativeElement)
      .draggable({
        modifiers: [
          interact.modifiers.snap({
            targets: [interact.snappers.grid({ x: slotWidth, y: -1 })],
            offset: 'startCoords',
          }),
        ],
      })
      .on('dragstart', this.handleDragStart.bind(this))
      .on('dragmove', this.handleDragMove.bind(this))
      .on('dragend', this.finishDrag.bind(this))
      .styleCursor(false);
  }

  private handleDragStart(event: Interact.DragEvent): void {
    const dragOrResizeHandleHTML = event.currentTarget as HTMLDivElement;
    this.interaction = dragOrResizeHandleHTML.classList.contains(ResizeHandlePlannerEvent)
      ? DragOrResizeInteraction.Resize
      : DragOrResizeInteraction.Drag;
    const currentlyDraggedPlannerEventId = +this.shiveRenderService.getAttribute(
      dragOrResizeHandleHTML,
      'data-event-id',
    );
    assert(currentlyDraggedPlannerEventId !== null, 'Planner id is invalid.');

    this.plannerEventBeforeDrag = this.plannerEventCrudService.selectPlannerEvent(
      currentlyDraggedPlannerEventId,
    );

    this.plannerEventBeforeDrag = clone(this.plannerEventBeforeDrag);
    assert(this.plannerEventBeforeDrag !== null, 'Event Model is invalid.');

    // Add active drag / resize CSS class to planner event wrapper;
    const plannerEventWrapper = this.plannerEventBeforeDrag.componentRef.instance.eventWrapper();
    this.shiveRenderService.addClass(plannerEventWrapper.nativeElement, ActiveDragOrResizeEvent);

    // Add no-select CSS class so that the whole text of the page is not selectable
    this.shiveRenderService.addClass(this.document.body, 'noselect');

    // For resizing, get the left position
    this.beforeResizeLeftPos = this.plannerEventBeforeDrag.componentRef.instance.eventLeft();

    // Store mouse slot index when drag starts.
    this.beforeDragMouseSlotIndex = this.calculateMouseSlotIndex(event);

    // Update planner CSS class.
    this.absencePlannerService.dragOrResizeInteractionInProgress$.next(true);
  }

  private handleDragMove(event: Interact.DragEvent): void {
    const target = event.target;

    if (this.interaction === DragOrResizeInteraction.Drag) {
      if (event.dx === 0) {
        return;
      }

      let dataX = +this.shiveRenderService.getAttribute(target, 'data-x');
      dataX = (dataX || 0) + event.dx;
      this.shiveRenderService.moveElement(target, dataX);
      this.shiveRenderService.setAttribute(target, 'data-x', dataX.toString());
      target.setAttribute('data-x', dataX.toString());
    } else {
      this.resizeTimeBlock(event);
    }
  }

  private calculateMouseSlotIndex(event: Interact.DragEvent): number {
    assert(this.slotWidth !== null && this.slotWidth > 0, 'Slot width is null.');

    const htmlGrid = this.absencePlannerService.plannerModel.htmlGrid;
    const offsetLeft =
      Math.abs(htmlGrid.getBoundingClientRect().left) + AbsencePlannerFixedColumnWidth;
    const plannerMousePositionX = event.pageX - offsetLeft;
    return Math.floor(plannerMousePositionX / this.slotWidth);
  }

  private resizeTimeBlock(event: Interact.DragEvent) {
    this.currentlyDraggedEventModel = this.plannerEventCrudService.selectPlannerEvent(
      this.plannerEventBeforeDrag.id,
    );

    const currentMouseSlotIndex = this.calculateMouseSlotIndex(event);
    const dayDiff = currentMouseSlotIndex - this.beforeDragMouseSlotIndex;
    const isLeftDragHandle = event.target.classList.contains(LeftHandlePlannerEvent);
    let startTime = this.currentlyDraggedEventModel.start;
    let endTime = this.currentlyDraggedEventModel.end;

    if (isLeftDragHandle) {
      this.currentlyDraggedEventModel.start = DateTimeHelper.addDays(startTime, dayDiff);
    } else {
      this.currentlyDraggedEventModel.end = DateTimeHelper.addDays(endTime, dayDiff);
    }

    startTime = this.currentlyDraggedEventModel.start;
    endTime = this.currentlyDraggedEventModel.end;

    // Reset when drag handler overlap.
    if (DateTimeHelper.isAfter(startTime, endTime)) {
      this.currentlyDraggedEventModel.start = DateTimeHelper.startOfDay(
        this.currentlyDraggedEventModel.start,
      );
      DateTimeHelper.startOfNextDay(this.currentlyDraggedEventModel.start);
      return;
    }

    this.plannerEventComponentHandlerService.updateComponentWhileResizing(
      this.currentlyDraggedEventModel,
      isLeftDragHandle,
      dayDiff,
      this.beforeResizeLeftPos,
    );
  }

  private finishDrag(event: DragEvent): void {
    event.stopPropagation();

    let currentlyDraggedEventModel = this.plannerEventCrudService.selectPlannerEvent(
      this.plannerEventBeforeDrag.id,
    );

    if (this.interaction === DragOrResizeInteraction.Drag) {
      assert(currentlyDraggedEventModel !== null, 'Planner event model is null.');

      const currentMouseSlotIndex = this.calculateMouseSlotIndex(event);
      const dayDiff = currentMouseSlotIndex - this.beforeDragMouseSlotIndex;
      currentlyDraggedEventModel.start = DateTimeHelper.addDays(
        currentlyDraggedEventModel.start,
        dayDiff,
      );
      currentlyDraggedEventModel.end = DateTimeHelper.addDays(
        currentlyDraggedEventModel.end,
        dayDiff,
      );
    } else {
      // Resize
      currentlyDraggedEventModel = this.currentlyDraggedEventModel;
    }

    // Remove active drag / resize CSS class to planner event wrapper;
    const plannerEventWrapper = this.plannerEventBeforeDrag.componentRef.instance.eventWrapper();
    this.shiveRenderService.removeClass(plannerEventWrapper.nativeElement, ActiveDragOrResizeEvent);

    // Update the planner event in the planner model.
    this.plannerEventCrudService.updatePlannerEvent(currentlyDraggedEventModel);
    // Conduct PUT http request
    const timeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      currentlyDraggedEventModel as unknown as TimeBlockAbsenceModel<TimeBlockComponent>,
      TimeBlockContentType.Absence,
    );
    this.absencePlannerService.timeBlockHttpService.putTimeBlock(timeBlock);

    this.shiveRenderService.removeClass(this.document.body, 'noselect');
    this.absencePlannerService.dragOrResizeInteractionInProgress$.next(false);
    this.currentlyDraggedEventModel = null;
  }

  private initHttpCallbacks(): void {
    this.actions$
      .pipe(ofType(fromTimeBlockActions.timeBlockUpdated, fromTimeBlockActions.httpFail))
      .subscribe(({ type }) => {
        if (type === fromTimeBlockActions.timeBlockUpdated.type) {
          this.shiveNotificationService.show(NotificationType.Success);
          return;
        }

        // Print an error notification
        this.shiveNotificationService.show(NotificationType.Error, 'Error occurred.');
      });
  }
}
